import type {
  ComprovanteUpload,
  NegociacaoRequest,
  NegociacaoResponse,
  NotificacaoRecipientView,
  RecipientFilters,
} from '../../types/recipient.types';
import {
  filtrarNotificacoes,
  getNotificacaoById,
  mockNotificacoesRecebidas,
} from '../lib/mockData';

interface PaginatedResponse {
  data: NotificacaoRecipientView[];
  total: number;
  page: number;
  limit: number;
}

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export async function getNotificacoesRecebidas(
  filters: RecipientFilters,
): Promise<PaginatedResponse> {
  await delay(800);

  const filtradas = filtrarNotificacoes(mockNotificacoesRecebidas, {
    status: filters.status,
    ano: filters.ano,
    search: filters.search,
  });

  const page = filters.page || 1;
  const limit = filters.limit || 10;
  const start = (page - 1) * limit;
  const end = start + limit;

  const paginadas = filtradas.slice(start, end);

  return {
    data: paginadas,
    total: filtradas.length,
    page,
    limit,
  };
}

export async function getNotificacaoDetail(id: string): Promise<NotificacaoRecipientView> {
  await delay(500);

  const notificacao = getNotificacaoById(id);

  if (!notificacao) {
    throw new Error('Notificação não encontrada');
  }

  return notificacao;
}

export async function marcarComoLida(id: string): Promise<void> {
  await delay(300);

  const notificacao = mockNotificacoesRecebidas.find((n) => n.id === id);
  if (notificacao) {
    notificacao.foiLida = true;
    notificacao.dataLeitura = new Date().toISOString();
  }
}

export async function enviarComprovante(
  notificacaoId: string,
  data: ComprovanteUpload,
): Promise<{ id: string; url: string; createdAt: string }> {
  await delay(1500);

  if (Math.random() > 0.9) {
    throw new Error('Erro ao fazer upload do comprovante. Tente novamente.');
  }

  const notificacao = mockNotificacoesRecebidas.find((n) => n.id === notificacaoId);
  if (notificacao) {
    notificacao.comprovanteEnviado = true;
  }

  return {
    id: `comp-${Date.now()}`,
    url: `/comprovantes/${notificacaoId}/${data.arquivo.name}`,
    createdAt: new Date().toISOString(),
  };
}

export async function solicitarNegociacao(
  notificacaoId: string,
  _data: NegociacaoRequest,
): Promise<NegociacaoResponse> {
  await delay(1000);

  if (Math.random() > 0.95) {
    throw new Error('Erro ao solicitar negociação. Tente novamente.');
  }

  const notificacao = mockNotificacoesRecebidas.find((n) => n.id === notificacaoId);
  if (notificacao) {
    notificacao.negociacaoAberta = true;
  }

  const protocolo = `NEG-${new Date().getFullYear()}-${String(Math.floor(Math.random() * 99999)).padStart(5, '0')}`;

  return {
    id: `neg-${Date.now()}`,
    notificacaoId,
    protocolo,
    status: 'pendente',
    createdAt: new Date().toISOString(),
  };
}

export async function downloadTodosDocumentos(_notificacaoId: string): Promise<Blob> {
  await delay(1000);

  const dummyZip = new Blob(['ZIP content here'], { type: 'application/zip' });
  return dummyZip;
}
